<template>
  <div class="interactive-reading-container">
    <div class="reading-header">
      <div class="header-info">
        <h2>Interactive Reading Session</h2>
        <p v-if="psychicName">Connected with {{ psychicName }}</p>
        <p v-else>Waiting for psychic to join...</p>
      </div>
      <div class="header-controls">
        <button class="control-btn" @click="clearCanvas">Clear</button>
        <button class="control-btn end-btn" @click="endSession">End Session</button>
      </div>
    </div>

    <div class="reading-canvas-wrapper" ref="canvasWrapper">
      <canvas
        ref="touchCanvas"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
        class="touch-canvas"
      ></canvas>
      
      <div class="connection-status" :class="{ connected: isConnected }">
        <div class="status-indicator"></div>
        <span>{{ isConnected ? 'Connected' : 'Waiting for connection...' }}</span>
      </div>

      <div class="user-indicators">
        <div class="user-indicator you">
          <div class="indicator-dot"></div>
          <span>You</span>
        </div>
        <div class="user-indicator psychic">
          <div class="indicator-dot"></div>
          <span>Psychic</span>
        </div>
      </div>
    </div>

    <div class="reading-info">
      <div class="info-card">
        <h3>Touch Points</h3>
        <div class="touch-count">
          <span class="count-value">{{ yourTouchCount }}</span>
          <span class="count-label">Your touches</span>
        </div>
        <div class="touch-count">
          <span class="count-value">{{ psychicTouchCount }}</span>
          <span class="count-label">Psychic touches</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { firestore } from '@/firebase';
import { getAuth } from 'firebase/auth';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

export default {
  name: 'InteractiveReadingModule',
  props: {
    psychicId: {
      type: String,
      required: true
    },
    psychicName: {
      type: String,
      default: 'Psychic'
    },
    sessionId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isConnected: false,
      yourTouchCount: 0,
      psychicTouchCount: 0,
      activeTouches: new Map(),
      psychicTouches: new Map(),
      isDrawing: false,
      currentSessionId: null,
      currentUserId: null,
      ctx: null,
      unsubscribeSession: null,
      TOUCH_COLORS: {
        you: '#DB8FFF',
        psychic: '#FFCD00'
      },
      TOUCH_RADIUS: 30
    };
  },
  methods: {
    initCanvas() {
      const canvas = this.$refs.touchCanvas;
      const wrapper = this.$refs.canvasWrapper;
      if (!canvas || !wrapper) return;

      canvas.width = wrapper.clientWidth;
      canvas.height = wrapper.clientHeight;

      this.ctx = canvas.getContext('2d');
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
    },

    drawTouch(x, y, color, identifier) {
      if (!this.ctx) return;

      const gradient = this.ctx.createRadialGradient(
        x, y, 0,
        x, y, this.TOUCH_RADIUS * 2
      );
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.5, `${color}80`);
      gradient.addColorStop(1, `${color}00`);

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(x, y, this.TOUCH_RADIUS * 2, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = color;
      this.ctx.beginPath();
      this.ctx.arc(x, y, this.TOUCH_RADIUS, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = 'rgba(255,255,255,0.3)';
      this.ctx.beginPath();
      this.ctx.arc(
        x - this.TOUCH_RADIUS * 0.3,
        y - this.TOUCH_RADIUS * 0.3,
        this.TOUCH_RADIUS * 0.4,
        0,
        Math.PI * 2
      );
      this.ctx.fill();

      this.ctx.fillStyle = '#000000';
      this.ctx.font = 'bold 16px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(identifier.toString(), x, y);
    },

    clearCanvas() {
      const canvas = this.$refs.touchCanvas;
      if (!canvas || !this.ctx) return;

      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);

      this.activeTouches.clear();
      this.psychicTouches.clear();
      this.yourTouchCount = 0;
      this.psychicTouchCount = 0;

      if (this.currentSessionId) {
        const sessionRef = doc(firestore, 'readingSessions', this.currentSessionId);
        setDoc(
          sessionRef,
          {
            [`userTouches_${this.currentUserId}`]: {},
            lastUpdate: serverTimestamp()
          },
          { merge: true }
        );
      }
    },

    getTouchPosition(e) {
      const canvas = this.$refs.touchCanvas;
      const rect = canvas.getBoundingClientRect();

      if (e.touches && e.touches.length) {
        return Array.from(e.touches).map((touch) => ({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
          identifier: touch.identifier
        }));
      }

      return [
        {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          identifier: 'mouse'
        }
      ];
    },

    handleTouchStart(e) {
      e.preventDefault();
      this.isDrawing = true;

      const touches = this.getTouchPosition(e);
      touches.forEach((touch) => {
        this.activeTouches.set(touch.identifier, touch);
        this.drawTouch(touch.x, touch.y, this.TOUCH_COLORS.you, this.activeTouches.size);
        this.yourTouchCount = this.activeTouches.size;
      });

      this.sendTouchesToFirestore();
    },

    handleTouchMove(e) {
      e.preventDefault();
      if (!this.isDrawing) return;

      const touches = this.getTouchPosition(e);
      this.redrawCanvas();

      touches.forEach((touch) => {
        if (this.activeTouches.has(touch.identifier)) {
          this.activeTouches.set(touch.identifier, touch);
          const index =
            Array.from(this.activeTouches.keys()).indexOf(touch.identifier) + 1;

          this.drawTouch(touch.x, touch.y, this.TOUCH_COLORS.you, index);
        }
      });

      this.sendTouchesToFirestore();
    },

    handleTouchEnd(e) {
      e.preventDefault();

      const touches = this.getTouchPosition(e);
      touches.forEach((touch) => {
        this.activeTouches.delete(touch.identifier);
      });

      if (this.activeTouches.size === 0) {
        this.isDrawing = false;
      }

      this.yourTouchCount = this.activeTouches.size;
      this.redrawCanvas();
      this.sendTouchesToFirestore();
    },

    handleMouseDown(e) {
      this.isDrawing = true;
      const pos = this.getTouchPosition(e)[0];
      this.activeTouches.set('mouse', pos);
      this.drawTouch(pos.x, pos.y, this.TOUCH_COLORS.you, 1);
      this.yourTouchCount = this.activeTouches.size;
      this.sendTouchesToFirestore();
    },

    handleMouseMove(e) {
      if (!this.isDrawing) return;

      const pos = this.getTouchPosition(e)[0];
      if (this.activeTouches.has('mouse')) {
        this.redrawCanvas();
        this.activeTouches.set('mouse', pos);
        this.drawTouch(pos.x, pos.y, this.TOUCH_COLORS.you, 1);
        this.sendTouchesToFirestore();
      }
    },

    handleMouseUp() {
      this.activeTouches.delete('mouse');
      this.isDrawing = false;
      this.yourTouchCount = this.activeTouches.size;
      this.redrawCanvas();
      this.sendTouchesToFirestore();
    },

    redrawCanvas() {
      const canvas = this.$refs.touchCanvas;
      if (!canvas || !this.ctx) return;

      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);

      let yourIndex = 1;
      this.activeTouches.forEach((touch) => {
        this.drawTouch(touch.x, touch.y, this.TOUCH_COLORS.you, yourIndex++);
      });

      let psychicIndex = 1;
      this.psychicTouches.forEach((touch) => {
        this.drawTouch(touch.x, touch.y, this.TOUCH_COLORS.psychic, psychicIndex++);
      });
    },

    sendTouchesToFirestore() {
      if (!this.currentSessionId || !this.currentUserId) return;

      const sessionRef = doc(firestore, 'readingSessions', this.currentSessionId);
      const touchData = {};

      this.activeTouches.forEach((t, id) => {
        touchData[id] = { x: t.x, y: t.y };
      });

      setDoc(
        sessionRef,
        {
          [`userTouches_${this.currentUserId}`]: touchData,
          lastUpdate: serverTimestamp()
        },
        { merge: true }
      );
    },

    listenForPsychicTouches() {
      if (!this.currentSessionId || !this.psychicId) return;

      const sessionRef = doc(firestore, 'readingSessions', this.currentSessionId);

      this.unsubscribeSession = onSnapshot(sessionRef, (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();
        
        // Check if session was ended by the other participant
        if (data.ended === true && data.endedBy !== this.currentUserId) {
          // Session was ended by the other person
          alert('The other participant has ended the session.');
          this.$emit('close');
          return;
        }
        
        // Update connection status
        this.isConnected = data.status === 'active' || data.psychicJoined === true;
        
        const key = `userTouches_${this.psychicId}`;
        const touches = data[key] || {};

        this.psychicTouches.clear();

        Object.keys(touches).forEach((id) => {
          this.psychicTouches.set(id, touches[id]);
        });

        this.psychicTouchCount = this.psychicTouches.size;
        this.redrawCanvas();
      });
    },

    async endSession() {
      // Mark session as ended in Firestore
      if (this.currentSessionId) {
        try {
          const sessionRef = doc(firestore, 'readingSessions', this.currentSessionId);
          await setDoc(
            sessionRef,
            {
              ended: true,
              endedBy: this.currentUserId,
              endedAt: serverTimestamp(),
              lastUpdate: serverTimestamp()
            },
            { merge: true }
          );
        } catch (error) {
          console.error("Error ending session:", error);
        }
      }
      
      // Clean up local state
      this.activeTouches.clear();
      this.psychicTouches.clear();
      this.yourTouchCount = 0;
      this.psychicTouchCount = 0;
      
      // Emit close event
      this.$emit('close');
    }
  },

  async mounted() {
    this.initCanvas();

    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      this.currentUserId = user.uid;
    }

    // Use provided sessionId or create new one
    if (this.sessionId) {
      this.currentSessionId = this.sessionId;
    } else {
      this.currentSessionId = `${this.currentUserId}_${this.psychicId}`;
      
      // Create session document immediately when user starts
      try {
        const sessionRef = doc(firestore, 'readingSessions', this.currentSessionId);
        await setDoc(
          sessionRef,
          {
            userId: this.currentUserId,
            psychicId: this.psychicId,
            createdAt: serverTimestamp(),
            status: 'awaiting',
            ended: false,
            lastUpdate: serverTimestamp()
          },
          { merge: true }
        );
      } catch (error) {
        console.error("Error creating session:", error);
      }
    }
    this.listenForPsychicTouches();
  },

  beforeUnmount() {
    if (this.unsubscribeSession) this.unsubscribeSession();
  }
};
</script>

<style scoped>
.interactive-reading-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #000000;
  color: #ECC3FF;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
}

.reading-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  background: linear-gradient(135deg, rgba(42, 9, 58, 0.95) 0%, rgba(22, 4, 30, 0.95) 100%);
  border-bottom: 2px solid rgba(219, 143, 255, 0.2);
}

.header-info h2 {
  font-size: 24px;
  font-weight: 700;
  color: #DB8FFF;
  margin: 0 0 8px 0;
}

.header-info p {
  font-size: 14px;
  color: #ECC3FF;
  opacity: 0.8;
  margin: 0;
}

.header-controls {
  display: flex;
  gap: 12px;
}

.control-btn {
  padding: 10px 20px;
  background: rgba(219, 143, 255, 0.2);
  border: 2px solid rgba(219, 143, 255, 0.3);
  border-radius: 8px;
  color: #ECC3FF;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(219, 143, 255, 0.3);
  border-color: #DB8FFF;
  transform: translateY(-2px);
}

.end-btn {
  background: rgba(255, 77, 77, 0.2);
  border-color: rgba(255, 77, 77, 0.3);
  color: #FFCD00;
}

.end-btn:hover {
  background: rgba(255, 77, 77, 0.3);
  border-color: #FF5555;
}

.reading-canvas-wrapper {
  flex: 1;
  position: relative;
  background: #000000;
  overflow: hidden;
  touch-action: none;
}

.touch-canvas {
  width: 100%;
  height: 100%;
  display: block;
  cursor: crosshair;
  touch-action: none;
}

.connection-status {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  border: 2px solid rgba(255, 77, 77, 0.5);
}

.connection-status.connected {
  border-color: rgba(76, 175, 80, 0.5);
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #FF5555;
  animation: pulse 2s infinite;
}

.connection-status.connected .status-indicator {
  background: #4CAF50;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.user-indicators {
  position: absolute;
  bottom: 20px;
  left: 20px;
  display: flex;
  gap: 20px;
}

.user-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  border: 2px solid;
}

.user-indicator.you {
  border-color: rgba(219, 143, 255, 0.5);
}

.user-indicator.psychic {
  border-color: rgba(255, 205, 0, 0.5);
}

.indicator-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.user-indicator.you .indicator-dot {
  background: #DB8FFF;
  box-shadow: 0 0 10px rgba(219, 143, 255, 0.8);
}

.user-indicator.psychic .indicator-dot {
  background: #FFCD00;
  box-shadow: 0 0 10px rgba(255, 205, 0, 0.8);
}

.reading-info {
  padding: 20px 32px;
  background: linear-gradient(135deg, rgba(42, 9, 58, 0.95) 0%, rgba(22, 4, 30, 0.95) 100%);
  border-top: 2px solid rgba(219, 143, 255, 0.2);
}

.info-card {
  display: flex;
  gap: 40px;
  align-items: center;
}

.info-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: #DB8FFF;
  margin: 0;
}

.touch-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.count-value {
  font-size: 32px;
  font-weight: 800;
  color: #DB8FFF;
  line-height: 1;
}

.count-label {
  font-size: 12px;
  color: #ECC3FF;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

@media (max-width: 768px) {
  .reading-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-controls {
    width: 100%;
  }

  .control-btn {
    flex: 1;
  }

  .info-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .touch-count {
    flex-direction: row;
    gap: 12px;
  }
}
</style>

